/**
 * Variant-B trade market. Entered from either the runner HUD (mid-session,
 * pauses RunnerScene) or the Launcher tile (between sessions). Hero walks
 * freely L/R + jumps inside a 1-screen stone room; a trade-board fixture on
 * the right wall shows 5 cards from the rolling offer buffer with a Up/Down
 * cursor. Y/K accepts the highlighted offer; Esc / left-portal exits back
 * to the caller.
 *
 * Wallet source switches by entry point:
 *   - "runner" → RunnerScene exposes its RunnerWallet through the registry
 *     before pausing; reads/writes route through the scene's existing state.
 *   - "launcher" → uses a persistent LauncherWallet snapshot stored in the
 *     registry. Trades on the launcher side are not yet propagated into a
 *     SessionConfig handoff to the runner (see TODO note in the design doc).
 */

import Phaser from "phaser";

import { GAMEPLAY, NATIVE_HEIGHT, NATIVE_WIDTH, PALETTE, UGC_CYAN } from "../config";
import type { AuthorIdentity, OfferAffordabilityTier, TradeOffer } from "../types";

import { AudioService } from "../services/AudioService";
import { AuthorIdentityService } from "../services/AuthorIdentityService";
import { LocationService, type LocationInfo } from "../services/LocationService";
import { OFFER_LIFETIME_MS, ShopService } from "../services/ShopService";
import { ProfileTooltipService } from "../services/ProfileTooltipService";
import {
  LauncherWallet,
  makeDefaultWalletSnapshot,
  offerIsAffordable,
  resourcesToDelta,
  type Wallet,
  type WalletSnapshot,
} from "../services/Wallet";

import { AuthorBadge } from "../ui/AuthorBadge";
import { OFFER_CARD_HEIGHT, OFFER_CARD_WIDTH, OfferCard } from "../ui/OfferCard";
import { WalletHud } from "../ui/WalletHud";
import {
  PlatformBoardScene,
  type BoardCard,
  type BoardSceneInit,
  type DoorsConfig,
} from "./PlatformBoardScene";
import type { BoardTab, SpawnEntry } from "./board/navigation";
import { t } from "../i18n";

const FLOOR_Y = NATIVE_HEIGHT - 28;
/** Centered card-stack column x (matches the shared board geometry). Used to
 *  place offer cards + their full-card profile-hover hit zones. */
const CARD_STACK_X = Math.round((NATIVE_WIDTH - 200) / 2);
const DEPTH_HUD = 20;
/** How often the location widget re-checks the GPS fix. Each tick pulses the
 *  pin and re-polls; the placename only changes when the fix moves >30 m. */
const LOCATION_REFRESH_MS = 5_000;

export class ShopScene extends PlatformBoardScene {
  static readonly KEY = "ShopScene";

  protected activeTab: BoardTab = "trades";

  private shop!: ShopService;
  private identity!: AuthorIdentityService;
  private wallet!: Wallet;
  private audio!: AudioService;
  /** Unsub handles for the live offer buffer subscriptions. */
  private unsubNewOffer?: () => void;
  private unsubShop?: () => void;
  private telemetry!: {
    emit: (layer: 1 | 2 | 3, tag: string, data?: Record<string, number | string | boolean>) => void;
  };

  /** Location compass widget (bottom-right) — needle glyph rotates to a
   *  bearing, label shows the Nominatim placename for the local-GPS fix. */
  private locationNeedle?: Phaser.GameObjects.Text;
  private locationLabel?: Phaser.GameObjects.Text;
  /** Placename currently shown — lets the 5 s re-check skip relabel + telemetry
   *  when a stationary fix resolves to the same place. */
  private lastPlaceName?: string;
  /** Resting rotation (rad) of the pin = the current bearing. The per-update
   *  nudge wiggles around this and settles back to it. */
  private needleRestRotation = 0;
  /** One-shot rotation nudge played on each location re-check. */
  private needleTickTween?: Phaser.Tweens.Tween;
  /** Fictive "N players online" counter — flavour text. Drifts ±1 between
   *  71 and 77 every ~10–20 s. */
  private playersOnline = 74;
  private playersOnlineText!: Phaser.GameObjects.Text;
  /** Shared wallet HUD — identical layout to the runner's HUD so the
   *  player can see what they have to spend without leaving the market. */
  private walletHud!: WalletHud;
  /** Action prompt floating above the hero — shows "[Y] TRADE" on an
   *  affordable offer or a missing-resource hint on an unaffordable one. */
  private actionPrompt!: Phaser.GameObjects.Container;
  private actionPromptBg!: Phaser.GameObjects.Rectangle;
  private actionPromptText!: Phaser.GameObjects.Text;

  constructor() {
    super({ key: ShopScene.KEY, physics: { arcade: { gravity: { x: 0, y: GAMEPLAY.gravity } } } });
  }

  init(data: Partial<BoardSceneInit>): void {
    this.initBoard(data);
  }

  create(): void {
    this.audio = new AudioService(this);
    // Audio context starts suspended until first user gesture.
    this.input.keyboard?.once("keydown", () => this.audio.resume());
    this.input.once("pointerdown", () => this.audio.resume());

    // Resolve services from the registry (set by BootScene / scenes earlier).
    this.identity = this.registry.get("authorIdentityService") as AuthorIdentityService;
    if (!this.identity) {
      this.identity = new AuthorIdentityService();
      this.registry.set("authorIdentityService", this.identity);
    }
    const telemetry = this.registry.get("telemetryService") as
      | { emit: (layer: 1 | 2 | 3, tag: string, data?: Record<string, number | string | boolean>) => void }
      | undefined;
    this.telemetry = telemetry ?? { emit: () => undefined };

    this.shop = ShopService.getOrCreate({
      game: this.game,
      identity: this.identity,
      getCurrentWallet: () => this.wallet,
      emitTelemetry: (l, t, d) => this.telemetry.emit(l, t, d),
      now: () => this.time.now,
    });

    this.wallet = this.resolveWallet();

    // Room dressing first (behind everything), then the shared board
    // (hero + platform stack + scroll indicator + doors + tab strip + cards).
    this.buildRoomBackground();
    this.buildBoard();
    this.buildPlayersOnline();
    this.buildWalletHud();
    this.buildActionPrompt();
    this.buildLocationWidget();
    this.updateWalletHud();

    // Location-based market: poll the local GPS endpoint, reverse-geocode via
    // Nominatim, and fill the compass widget once resolved. Cached, so a
    // re-entry within the session is instant.
    void LocationService.resolve().then((info) => {
      if (this.scene.isActive()) this.applyLocation(info);
    });
    this.scheduleLocationRefresh();

    this.unsubShop = this.shop.subscribe(() => this.renderCards());
    this.unsubNewOffer = this.shop.onNewOffer(() => {
      // The render call from subscribe() runs first; this fires after
      // and gives us the chance to animate the freshly placed top card.
      this.time.delayedCall(0, () => {
        this.audio.shopOfferArrived();
        const standingOnOffer = this.selectedIndex() >= 0;
        if (!standingOnOffer && this.scrollOffset !== 0) {
          // Auto-scroll to top with a curtain sweep (old cards drop off
          // the bottom, new cards drop in from above). Single visual
          // animation regardless of how far we're scrolling.
          this.sweepToOffset(0);
          return;
        }
        if (this.scrollOffset === 0) {
          this.cards[0]?.animateArrive?.();
        } else {
          this.maybeFlashOutOfViewHint();
        }
      });
    });

    // Phaser does NOT auto-call a Scene's `shutdown()` method — it only emits
    // the SHUTDOWN event — so wire the cleanup explicitly. Without this the
    // ShopService subscription leaks past `scene.stop()`, and the service's
    // game-level PRE_STEP `notify()` keeps calling `renderCards()` on the
    // torn-down scene (crashing in ScrollIndicator on destroyed GameObjects).
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, this.shutdown, this);

    this.telemetry.emit(1, "ugc.shop.enter", {
      returnTo: this.returnTo,
      walletSource: this.walletSource,
    });
  }

  // ---- PlatformBoardScene hooks -------------------------------------------

  protected visibleRows(): number {
    return GAMEPLAY.shopBoardVisibleCards;
  }

  protected itemCount(): number {
    return this.shop.offers.length;
  }

  protected createCard(itemIndex: number, x: number, y: number): BoardCard {
    const offer = this.shop.offers[itemIndex]!;
    const identity = this.identity.forSeed(offer.authorAvatarSeed);
    const card = new OfferCard(this, x, y, offer, identity, this.identity);
    card.setTier(this.tierFor(offer));
    this.attachProfileHover(card, offer.authorAvatarSeed, x, y);
    return {
      container: card.container,
      setSelected: (s) => card.setHighlighted(s),
      destroy: () => card.destroy(),
      animateArrive: () => card.animateArrive(),
      tick: (now) => card.setCountdown(OFFER_LIFETIME_MS - (now - offer.arrivedAtMs)),
      tweenTo: (x, y, d, cb) => card.tweenTo(x, y, d, cb),
      flashRejected: () => card.flashRejected(),
    };
  }

  protected configureDoors(): DoorsConfig {
    return {
      left: { action: "exitMarket", labelKey: "shop.exit", hintKey: "shop.hint.exit", render: "doorSprite" },
      right: { action: "toGallery", labelKey: "shop.toGlobe", render: "glowStrip" },
    };
  }

  protected onSelectConfirm(_selectedBufferIndex: number): void {
    // Buy on either interact (Y / K / gamepad Y) or attack (J / gamepad X).
    // The "selected" offer is the one whose platform the hero stands on;
    // pressing on the floor surfaces a one-line hint banner.
    this.tryAcceptStandingOffer();
  }

  protected override onScrolled(direction: "up" | "down", source: "arrow" | "jump" | "drop"): void {
    this.audio.shopScroll();
    const data: Record<string, number | string> = { direction, scrollOffset: this.scrollOffset };
    if (source !== "arrow") data.source = source;
    this.telemetry.emit(1, "ugc.shop.scroll", data);
  }

  protected override onJumped(): void {
    this.audio.jump();
  }

  protected override beforeRenderCards(): void {
    // Drop any tooltip pointing at a card we're about to destroy — its anchor
    // coordinates would be stale, and the destroyed card no longer emits
    // pointerout to clean up on its own.
    ProfileTooltipService.get(this.game)?.hide("forced");
  }

  protected override updateExtras(): void {
    this.updateActionPrompt();
    this.updateWalletHud();
  }

  protected exitMarket(): void {
    this.exit();
  }

  protected switchTab(target: BoardTab, entry: SpawnEntry): void {
    if (target !== "gallery") return;
    this.telemetry.emit(1, "ugc.gallery.open_from_shop", { via: this.switchVia });
    this.scene.start("GalleryScene", {
      returnTo: this.returnTo,
      walletSource: this.walletSource,
      entry,
    });
  }

  // maybeScrollBuffer / slideScrollAnimated / sweepToOffset now live in
  // PlatformBoardScene. The shop layers its scroll audio + telemetry on top
  // via onScrolled(), and drives sweepToOffset from the new-offer handler.

  shutdown(): void {
    this.unsubShop?.();
    this.unsubShop = undefined;
    this.unsubNewOffer?.();
    this.unsubNewOffer = undefined;
    for (const card of this.cards) card.destroy();
    this.cards = [];
  }

  // -- room / fixtures ------------------------------------------------------

  private buildRoomBackground(): void {
    // Dim back wall (full screen).
    this.add.rectangle(0, 0, NATIVE_WIDTH, NATIVE_HEIGHT, PALETTE.shopWall, 1).setOrigin(0, 0);
    // Slightly lighter wall band at the top for the banner.
    this.add.rectangle(0, 0, NATIVE_WIDTH, 24, PALETTE.shopWallHi, 1).setOrigin(0, 0);
    // Lantern dots — three small accent rectangles.
    for (let i = 0; i < 3; i++) {
      const x = 60 + i * 100;
      this.add.rectangle(x, 14, 4, 4, PALETTE.shopLantern, 1).setOrigin(0.5, 0.5);
      this.add.rectangle(x, 14, 10, 10, PALETTE.shopLanternGlow, 0.18).setOrigin(0.5, 0.5);
    }

    // Floor band.
    this.add.rectangle(0, FLOOR_Y, NATIVE_WIDTH, NATIVE_HEIGHT - FLOOR_Y, PALETTE.shopFloor, 1).setOrigin(0, 0);
    this.add.rectangle(0, FLOOR_Y, NATIVE_WIDTH, 1, PALETTE.shopFloorEdge, 1).setOrigin(0, 0);
    // Floor pattern: thin tile separators.
    for (let x = 16; x < NATIVE_WIDTH; x += 24) {
      this.add.rectangle(x, FLOOR_Y + 2, 1, NATIVE_HEIGHT - FLOOR_Y - 4, PALETTE.shopFloorEdge, 0.6).setOrigin(0, 0);
    }

    // Cyan UGC corner-mark.
    this.add.rectangle(4, 4, 4, 4, UGC_CYAN, 1).setOrigin(0, 0);

    // Bottom control hint — the tab strip and the exit/globe doors are drawn
    // by PlatformBoardScene. Sits a row above the bottom-right location widget.
    this.add
      .text(NATIVE_WIDTH / 2, NATIVE_HEIGHT - 20, t("shop.hint.controls"), {
        fontFamily: "monospace",
        fontSize: "6px",
        color: this.toHex(PALETTE.hudDim),
      })
      .setOrigin(0.5, 1)
      .setDepth(DEPTH_HUD);
  }

  /**
   * Location compass widget in the bottom-right corner — a small dial with a
   * rotating location-arrow needle and the placename beside it. Starts as
   * "locating…"; `applyLocation` fills it once the GPS fix + reverse-geocode
   * resolve (or the fallback kicks in).
   */
  private buildLocationWidget(): void {
    // Opaque bar flush to the bottom-right corner: placename on the left, the
    // compass needle on the right edge.
    const w = 140;
    const h = 16;
    const x0 = NATIVE_WIDTH - w;
    const y0 = NATIVE_HEIGHT - h;
    const cy = y0 + h / 2;
    this.add
      .rectangle(x0, y0, w, h, PALETTE.hudPanel, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(1, UGC_CYAN)
      .setDepth(DEPTH_HUD);
    const needleX = NATIVE_WIDTH - 9;
    this.locationNeedle = this.add
      .text(needleX, cy, "▲", {
        fontFamily: "monospace",
        fontSize: "10px",
        color: this.toHex(UGC_CYAN),
        fontStyle: "bold",
      })
      .setOrigin(0.5, 0.5)
      .setDepth(DEPTH_HUD + 1);
    this.locationLabel = this.add
      .text(needleX - 12, cy, t("shop.locating"), {
        fontFamily: "monospace",
        fontSize: "7px",
        color: this.toHex(PALETTE.parchment),
      })
      .setOrigin(1, 0.5)
      .setDepth(DEPTH_HUD + 1);

    // Gentle, always-on "breathing" so the pin reads as a live locator even
    // between the 5 s re-checks. Scale only — rotation is left to the bearing
    // + the per-update nudge. Loops until the scene shuts down.
    this.tweens.add({
      targets: this.locationNeedle,
      scale: { from: 1, to: 1.12 },
      duration: 1100,
      yoyo: true,
      repeat: -1,
      ease: "Sine.easeInOut",
    });
  }

  private applyLocation(info: LocationInfo): void {
    this.lastPlaceName = info.placeName;
    this.locationLabel?.setText(info.placeName);
    this.needleRestRotation = Phaser.Math.DegToRad(info.bearingDeg);
    this.locationNeedle?.setRotation(this.needleRestRotation);
    this.telemetry.emit(1, "ugc.shop.location", {
      placeName: info.placeName,
      source: info.source,
    });
  }

  /**
   * Re-check the GPS fix every {@link LOCATION_REFRESH_MS}. Each tick pings the
   * pin (a brief scale pulse so the player sees the widget is live) and re-polls
   * the location; the placename + telemetry only update when the place actually
   * changed. Recursive delayedCall — the scene clock cancels it on shutdown.
   */
  private scheduleLocationRefresh(): void {
    this.time.delayedCall(LOCATION_REFRESH_MS, () => {
      if (!this.scene.isActive()) return;
      this.nudgeLocationNeedle();
      void LocationService.refresh().then((info) => {
        if (this.scene.isActive() && info.placeName !== this.lastPlaceName) {
          this.applyLocation(info);
        }
      });
      this.scheduleLocationRefresh();
    });
  }

  /** Slight rotation wiggle around the resting bearing — the visible "I just
   *  re-checked" cue, played on each location re-check. Independent of the
   *  always-on scale pulse (different property), and settles back to rest. */
  private nudgeLocationNeedle(): void {
    const needle = this.locationNeedle;
    if (!needle) return;
    this.needleTickTween?.stop();
    needle.setRotation(this.needleRestRotation);
    this.needleTickTween = this.tweens.add({
      targets: needle,
      rotation: this.needleRestRotation + 0.16,
      duration: 150,
      yoyo: true,
      ease: "Sine.easeInOut",
    });
  }

  // spawnHero + the platform stack + one-way drop-through collider, and the
  // scroll indicator, are built by PlatformBoardScene.buildBoard().

  /** Flash the "NEW ▲" hint if the freshly arrived offer is not currently
   *  visible (the player is scrolled past it). Pulses 3× then fades. */
  private maybeFlashOutOfViewHint(): void {
    if (this.scrollOffset === 0) return;
    this.scrollIndicator.flashNewHint();
  }

  /**
   * Fictive "N players online" readout in the gap between the right edge
   * of the card stack and the scroll indicator. Random-walks ±1 every
   * ~10–20 s, clamped to [71, 77] so the number always feels alive but
   * roughly stable around the starting value.
   */
  private buildPlayersOnline(): void {
    // Pick a stable initial value within the band so different sessions
    // don't all open at 74.
    this.playersOnline = 71 + Math.floor(Math.random() * 7);
    // Strip lives between the right edge of the card stack and the scroll
    // track. Center the text in that gap.
    const stripCenterX = (CARD_STACK_X + 200 + (NATIVE_WIDTH - 18)) / 2;
    this.playersOnlineText = this.add
      .text(stripCenterX, 36, "", {
        fontFamily: "monospace",
        fontSize: "7px",
        color: this.toHex(PALETTE.parchment),
        align: "center",
      })
      .setOrigin(0.5, 0)
      .setDepth(DEPTH_HUD);
    this.refreshPlayersOnlineText();
    this.schedulePlayersOnlineDrift();
  }

  private refreshPlayersOnlineText(): void {
    this.playersOnlineText.setText(`${this.playersOnline}\nplayers\nonline`);
  }

  private schedulePlayersOnlineDrift(): void {
    const delayMs = 10_000 + Math.floor(Math.random() * 10_000); // 10–20 s
    this.time.delayedCall(delayMs, () => {
      // ±1 random walk clamped to [71, 77]. Subtle alpha pulse so the
      // change reads as motion, not a frame skip.
      const step = Math.random() < 0.5 ? -1 : 1;
      this.playersOnline = Phaser.Math.Clamp(this.playersOnline + step, 71, 77);
      this.refreshPlayersOnlineText();
      this.tweens.add({
        targets: this.playersOnlineText,
        alpha: { from: 0.4, to: 1 },
        duration: 320,
        ease: "Sine.easeOut",
      });
      this.schedulePlayersOnlineDrift();
    });
  }

  // -- wallet HUD ----------------------------------------------------------

  /**
   * Same WalletHud component the runner uses. Tooltips are skipped (the
   * market doesn't have the pointer-hover model), but the visual layout is
   * pixel-identical: HP rail + geo counter + skills strip + boot slot +
   * inventory slots in the exact same positions.
   */
  private buildWalletHud(): void {
    this.walletHud = new WalletHud(this);
    this.walletHud.setDepth(DEPTH_HUD);
  }

  private updateWalletHud(): void {
    if (!this.walletHud) return;
    this.walletHud.update({
      hp: this.wallet.hp,
      geo: this.wallet.geo,
      inventory: this.wallet.inventory,
      // The market only knows about tradeable skills (doubleJump,
      // escapeFeather); jump/attack/interact are always-on capabilities so
      // they render as owned. EscapeFeather doesn't have its own HUD icon
      // — feather charges show in the bottom rail in the runner; here the
      // player can still see them via the icons in the offer cards.
      skills: {
        jump: true,
        doubleJump: this.wallet.isSkillUnlocked("doubleJump"),
        attack: true,
        interact: true,
      },
      bootBoostActive: false,
    });
  }

  // -- action prompt -------------------------------------------------------

  /**
   * Builds the ambient "[Y] TRADE" / "INSUFFICIENT …" prompt that floats
   * above the hero whenever they're standing on a platform. Hidden when
   * the hero is on the floor / in the air.
   */
  private buildActionPrompt(): void {
    this.actionPromptBg = this.add
      .rectangle(0, 0, 90, 14, PALETTE.shopWall, 0.92)
      .setOrigin(0.5, 0.5)
      .setStrokeStyle(1, UGC_CYAN);
    this.actionPromptText = this.add
      .text(0, 0, "", {
        fontFamily: "monospace",
        fontSize: "8px",
        color: this.toHex(UGC_CYAN),
        fontStyle: "bold",
      })
      .setOrigin(0.5, 0.5);
    this.actionPrompt = this.add
      .container(0, 0, [this.actionPromptBg, this.actionPromptText])
      .setDepth(DEPTH_HUD + 2)
      .setVisible(false);
  }

  private updateActionPrompt(): void {
    const sel = this.selectedIndex();
    if (sel < 0) {
      this.actionPrompt.setVisible(false);
      return;
    }
    const offer = this.shop.offers[sel];
    if (!offer) {
      this.actionPrompt.setVisible(false);
      return;
    }
    const costDelta = resourcesToDelta(offer.cost);
    const receiveDelta = resourcesToDelta(offer.receive);
    const affordable = offerIsAffordable(this.wallet, costDelta, receiveDelta);
    if (affordable) {
      this.actionPromptText.setText(t("shop.action.trade"));
      this.actionPromptText.setColor(this.toHex(UGC_CYAN));
      this.actionPromptBg.setStrokeStyle(1, UGC_CYAN);
    } else {
      this.actionPromptText.setText(this.whyUnaffordable(offer));
      this.actionPromptText.setColor(this.toHex(0xff8080));
      this.actionPromptBg.setStrokeStyle(1, 0xff8080);
    }
    // Resize bg to fit the longest text we'd render.
    const w = Math.max(70, this.actionPromptText.width + 12);
    this.actionPromptBg.setSize(w, 14);
    this.actionPrompt.setPosition(this.hero.x, this.hero.y - 52);
    this.actionPrompt.setVisible(true);
  }

  private whyUnaffordable(offer: TradeOffer): string {
    const cost = resourcesToDelta(offer.cost);
    if (cost.geo !== undefined && this.wallet.geo < cost.geo) {
      return t("shop.need.geo", { n: cost.geo - this.wallet.geo });
    }
    if (cost.hp !== undefined && this.wallet.hp < cost.hp) {
      return t("shop.need.hp", { n: cost.hp - this.wallet.hp });
    }
    if (cost.featherCharges !== undefined && this.wallet.featherCharges < cost.featherCharges) {
      return t("shop.need.feather", { n: cost.featherCharges - this.wallet.featherCharges });
    }
    if (cost.inventory?.remove) {
      for (const itemId of cost.inventory.remove) {
        if (!this.wallet.inventory.some((it) => it.kind === itemId)) {
          return t("shop.need.item", { item: itemId.toUpperCase() });
        }
      }
    }
    if (cost.skills?.lose) {
      for (const id of cost.skills.lose) {
        if (!this.wallet.isSkillUnlocked(id)) {
          const skill = id === "doubleJump"
            ? t("shop.skill.short.double_jump")
            : t("shop.skill.short.feather");
          return t("shop.missing.skill", { skill });
        }
      }
    }
    return t("shop.action.insufficient");
  }

  // -- cards ---------------------------------------------------------------

  // renderCards is provided by PlatformBoardScene; the shop supplies per-card
  // construction (OfferCard + tier + profile hover + countdown) via createCard,
  // and hides any stale tooltip via beforeRenderCards().

  /**
   * Make the card hoverable for the profile tooltip. The hit zone covers the
   * full 200×36 card so the player doesn't have to aim at the tiny avatar
   * to discover the affordance. 200 ms hover delay (built into the service)
   * keeps casual cursor movement from flickering tooltips open.
   */
  private attachProfileHover(card: OfferCard, authorSeed: number, x: number, y: number): void {
    const profileSvc = ProfileTooltipService.get(this.game);
    if (!profileSvc) return;
    const container = card.container;
    container.setSize(OFFER_CARD_WIDTH, OFFER_CARD_HEIGHT);
    container.setInteractive(
      new Phaser.Geom.Rectangle(0, 0, OFFER_CARD_WIDTH, OFFER_CARD_HEIGHT),
      Phaser.Geom.Rectangle.Contains,
    );
    container.on("pointerover", () => {
      profileSvc.show(authorSeed, {
        source: "shop",
        trigger: "hover",
        anchor: { x, y, width: OFFER_CARD_WIDTH, height: OFFER_CARD_HEIGHT },
      });
    });
    container.on("pointerout", () => {
      profileSvc.cancelPending();
      profileSvc.hide("mouse_leave");
    });
  }

  // selectedIndex (standing buffer index), per-frame card ticks (countdowns
  // run via BoardCard.tick), and the selection highlight are provided by
  // PlatformBoardScene.

  private tryAcceptStandingOffer(): void {
    const sel = this.selectedIndex();
    if (sel < 0) {
      this.flashHintBanner(t("shop.hint.stand_on_platform"));
      this.audio.shopReject();
      return;
    }
    const offers = this.shop.offers;
    const offer = offers[sel];
    if (!offer) return;
    const costDelta = resourcesToDelta(offer.cost);
    const receiveDelta = resourcesToDelta(offer.receive);
    const affordable = offerIsAffordable(this.wallet, costDelta, receiveDelta);
    this.telemetry.emit(1, "ugc.shop.offer.accept.attempt", {
      offerId: offer.id,
      templateId: offer.templateId,
      affordable,
    });
    const platformIdx = sel - this.scrollOffset;
    const card = this.cards[platformIdx];
    if (affordable) {
      // Resources fly to/from the place where the offer's icons live (the
      // card the player accepted), not a generic bottom-center "register".
      const cardCenter = card
        ? {
            x: card.container.x + 78, // card width = 200, anchor on cost-side
            y: card.container.y + 24,
          }
        : { x: NATIVE_WIDTH / 2, y: FLOOR_Y - 12 };
      const receiveCenter = card
        ? {
            x: card.container.x + 150, // receive-side icons sit further right
            y: card.container.y + 24,
          }
        : cardCenter;
      this.animateResourcesFly(offer.cost, "out", cardCenter);
      this.shop.acceptOffer(offer.id, this.wallet);
      this.audio.shopTrade();
      this.time.delayedCall(160, () => this.animateResourcesFly(offer.receive, "in", receiveCenter));
      card?.tweenTo?.((card.container.x ?? 0) + 240, card.container.y, 220, () => card?.destroy());
      this.time.delayedCall(240, () => this.renderCards());
    } else {
      this.audio.shopReject();
      card?.flashRejected?.();
    }
  }

  /**
   * Spawn a flying icon per resource. "out" = HUD → bottom-center (the
   * register / counter); "in" = bottom-center → HUD. Used right after a
   * successful trade so the wallet change is visualised, not just numeric.
   */
  private animateResourcesFly(
    resources: readonly TradeOffer["cost"][number][],
    direction: "in" | "out",
    register: { x: number; y: number },
  ): void {
    let staggerMs = 0;
    for (const r of resources) {
      this.time.delayedCall(staggerMs, () => this.flyOne(r, direction, register));
      staggerMs += 80;
    }
  }

  private flyOne(
    r: TradeOffer["cost"][number],
    direction: "in" | "out",
    register: { x: number; y: number },
  ): void {
    const anchor = this.hudAnchorForResource(r);
    if (!anchor) return;
    const start = direction === "out" ? anchor : register;
    const end = direction === "out" ? register : anchor;
    const icon = this.add
      .image(start.x, start.y, this.iconKeyForResource(r))
      .setOrigin(0.5, 0.5)
      .setDepth(DEPTH_HUD + 5)
      .setScale(1.4);
    this.tweens.add({
      targets: icon,
      x: end.x,
      y: end.y,
      scale: direction === "out" ? 0.6 : 1.0,
      alpha: direction === "out" ? 0.2 : 1,
      duration: 380,
      ease: direction === "out" ? "Cubic.easeIn" : "Cubic.easeOut",
      onComplete: () => icon.destroy(),
    });
  }

  private hudAnchorForResource(r: TradeOffer["cost"][number]): { x: number; y: number } | undefined {
    switch (r.kind) {
      case "geo":
        return this.walletHud.geoAnchor();
      case "hp":
        return this.walletHud.hpAnchor(Math.max(0, this.wallet.hp - 1));
      case "feather":
        return this.walletHud.bootAnchor();
      case "inventoryItem": {
        const idx = Math.max(0, this.wallet.inventory.length - 1);
        return this.walletHud.inventoryAnchor(idx);
      }
      case "skill":
        return this.walletHud.skillAnchor(r.skillId === "doubleJump" ? "doubleJump" : "doubleJump");
    }
  }

  private iconKeyForResource(r: TradeOffer["cost"][number]): string {
    switch (r.kind) {
      case "geo":
        return "shopIcon.geo";
      case "hp":
        return "shopIcon.hp";
      case "feather":
        return "shopIcon.feather";
      case "inventoryItem":
        return "shopIcon.scroll";
      case "skill":
        return r.skillId === "doubleJump" ? "shopIcon.skillDoubleJump" : "shopIcon.skillEscapeFeather";
    }
  }

  private flashHintBanner(text: string): void {
    const t = this.add
      .text(NATIVE_WIDTH / 2, 32, text, {
        fontFamily: "monospace",
        fontSize: "8px",
        color: this.toHex(PALETTE.bannerText),
      })
      .setOrigin(0.5, 0.5)
      .setDepth(DEPTH_HUD);
    this.tweens.add({
      targets: t,
      alpha: 0,
      duration: 800,
      delay: 400,
      ease: "Quad.easeOut",
      onComplete: () => t.destroy(),
    });
  }

  private tierFor(offer: TradeOffer): OfferAffordabilityTier {
    const cost = resourcesToDelta(offer.cost);
    if (this.wallet.canAfford(cost)) return "own";
    // Projected: combine wallet + forecast and re-check.
    const forecast = this.wallet.forecastDelta(GAMEPLAY.shopForecastSeconds);
    const projectedWallet: Wallet = {
      geo: this.wallet.geo + (forecast.geo ?? 0),
      hp: this.wallet.hp + (forecast.hp ?? 0),
      featherCharges: this.wallet.featherCharges + (forecast.featherCharges ?? 0),
      inventory: this.wallet.inventory,
      isSkillUnlocked: (id) => this.wallet.isSkillUnlocked(id),
      canAfford: () => false,
      apply: () => undefined,
      forecastDelta: () => ({}),
    };
    if (projectedWallet.geo >= (cost.geo ?? 0) && projectedWallet.hp >= (cost.hp ?? 0) && projectedWallet.featherCharges >= (cost.featherCharges ?? 0)) {
      return "projected";
    }
    return "out";
  }

  private exit(): void {
    const dwellMs = this.time.now - this.enteredAtMs;
    this.telemetry.emit(1, "ugc.shop.exit", { returnTo: this.returnTo, dwellMs });
    if (this.returnTo === "runner") {
      this.scene.stop();
      this.scene.resume("runner");
    } else {
      this.scene.start("launcher");
    }
  }

  private resolveWallet(): Wallet {
    if (this.walletSource === "runner") {
      const runnerWallet = this.registry.get("runnerWallet") as Wallet | undefined;
      if (runnerWallet) return runnerWallet;
      // Fallback: launcher snapshot, so the scene never crashes.
    }
    let snapshot = this.registry.get("launcherWalletSnapshot") as WalletSnapshot | undefined;
    if (!snapshot) {
      snapshot = makeDefaultWalletSnapshot();
      // Seed some currency so launcher trades are interactive in v1.
      snapshot.geo = 50;
      snapshot.skills.doubleJump = true;
      this.registry.set("launcherWalletSnapshot", snapshot);
    }
    return new LauncherWallet(snapshot);
  }

  private toHex(c: number): string {
    return `#${c.toString(16).padStart(6, "0")}`;
  }
}

// Suppress no-unused-vars / no-explicit-any helper.
export type _ShopSceneUnused = AuthorBadge | AuthorIdentity;
